import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { useToast } from '../components/ui/Toast';

interface InvoiceSummary {
  id: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  status: 'IMPORTED' | 'VALIDATED' | 'CONTESTED';
  totalHT: string | null;
  lineCount: number;
  varianceCount: number;
  createdAt: string;
  supplierName: string;
  poNumber: string | null;
}

interface InvoiceLine {
  id: string;
  supplierArticleNo: string | null;
  description: string | null;
  quantity: string;
  unitPriceHT: string;
  poUnitPriceHT: string | null;
  variancePct: string | null;
  varianceFlag: boolean;
  ocrConfidence: string | null;
  productId: string | null;
  productSku: string | null;
  productName: string | null;
  supplierOrderLineId: string | null;
  poQuantity: number | null;
  poQuantityReceived: number | null;
}

interface InvoiceDetail extends Omit<InvoiceSummary, 'lineCount'> {
  lineCount: number;
  paymentDueDate: string | null;
  totalVat: string | null;
  totalTTC: string | null;
  ocrEngine: string | null;
  validatedAt: string | null;
  wapAppliedAt: string | null;
  supplierOrderId: string | null;
  pricingModel: 'NET_PRICE' | 'TARIFF_DISCOUNT';
  lines: InvoiceLine[];
}

interface PoOption {
  id: string;
  poNumber: string;
  supplierName: string;
  status: string;
}

const money = (v: string | null): string => (v == null ? '—' : `${Number(v).toFixed(2)} €`);

/**
 * Screen 40 (J2.9) — supplier invoice import and review.
 *
 * The layout is the spec's: extracted line on the left, the PO line it matched on the right, so
 * the reviewer compares rather than remembers. Variances over 5 % are red and, unlike a normal
 * warning, they BLOCK: validation is what moves the weighted average purchase price into
 * products.purchase_price_ht, and from there into every selling price the cascade computes. An
 * OCR misread of 9.00 as 90.00 that auto-applied would reprice the catalogue before anyone saw
 * the invoice. Each flagged line has to be accepted by name.
 *
 * With no OCR provider wired (the J6 open point), the screen says so and offers manual line
 * entry — which is the same review surface, just with the operator reading the paper.
 */
export default function SupplierInvoiceOcr() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [params, setParams] = useSearchParams();
  const fileRef = useRef<HTMLInputElement>(null);
  const reocrFileRef = useRef<HTMLInputElement>(null);

  const [ocr, setOcr] = useState<{ configured: boolean; engine: string } | null>(null);
  const [rows, setRows] = useState<InvoiceSummary[]>([]);
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [pos, setPos] = useState<PoOption[]>([]);
  const [poId, setPoId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const selectedId = params.get('id');

  const load = useCallback(async (): Promise<void> => {
    try {
      const [list, status, poList] = await Promise.all([
        apiClient.get<InvoiceSummary[]>('/supplier-invoices'),
        apiClient.get<{ configured: boolean; engine: string }>('/supplier-invoices/ocr/status'),
        apiClient.get<PoOption[]>('/purchasing/orders'),
      ]);
      setRows(list.data);
      setOcr(status.data);
      setPos(
        poList.data.filter(
          (p) => p.status === 'CONFIRMED' || p.status === 'SHIPPED' || p.status === 'RECEIVED'
        )
      );
    } catch (err) {
      showApiError(err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = useCallback(async (id: string): Promise<void> => {
    try {
      const { data } = await apiClient.get<InvoiceDetail>(`/supplier-invoices/${id}`);
      setDetail(data);
      setAccepted(new Set());
    } catch (err) {
      showApiError(err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const upload = async (): Promise<void> => {
    const file = fileRef.current?.files?.[0];
    if (!poId) return;
    setBusy(true);
    try {
      const fileBase64 = file ? await toBase64(file) : undefined;
      const { data } = await apiClient.post<{ id: string; varianceCount: number }>(
        '/supplier-invoices/ocr',
        {
          supplierOrderId: poId,
          fileBase64,
          filename: file?.name,
          invoiceNumber: invoiceNumber.trim() || undefined,
          // With no provider configured the server would extract nothing; send an empty manual
          // set so the invoice is created and the reviewer can key the lines from the paper.
          manualLines: fileBase64 && ocr?.configured ? undefined : [],
        }
      );
      push({
        tone: 'success',
        message: t('supplierInvoices.imported', { count: data.varianceCount }),
      });
      await load();
      setParams({ id: data.id });
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const flagged = detail?.lines.filter((l) => l.varianceFlag) ?? [];
  const allAccepted = flagged.every((l) => accepted.has(l.id));

  const validate = async (): Promise<void> => {
    if (!detail) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ wapUpdates: unknown[] }>(
        `/supplier-invoices/${detail.id}/validate`,
        { acceptVariances: [...accepted] }
      );
      push({
        tone: 'success',
        message: t('supplierInvoices.validated', { count: data.wapUpdates.length }),
      });
      await Promise.all([load(), loadDetail(detail.id)]);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Re-run the extraction on an invoice already imported.
   *
   * The PDF normally comes from the archive written at import, so the button is one click. Two
   * cases have no archive — invoices imported before the column existed, and imports made while
   * object storage was unconfigured — and the server answers those with 422. Rather than showing
   * the operator an error they can do nothing with, that answer opens the file picker: the only
   * missing thing is the document, and they have it.
   *
   * Not offered on a VALIDATED invoice. Validation is what wrote the weighted average into the
   * catalogue, and re-reading underneath it would leave the lines and the prices disagreeing.
   */
  const reocr = async (fileBase64?: string): Promise<void> => {
    if (!detail) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ lineCount: number; varianceCount: number }>(
        `/supplier-invoices/${detail.id}/reocr`,
        fileBase64 ? { fileBase64 } : {}
      );
      push({
        tone: 'success',
        message: t('supplierInvoices.reocrDone', {
          lines: data.lineCount,
          count: data.varianceCount,
        }),
      });
      await Promise.all([load(), loadDetail(detail.id)]);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 422 && !fileBase64) {
        push({ tone: 'info', message: t('supplierInvoices.reocrNeedsFile') });
        reocrFileRef.current?.click();
        return;
      }
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const onReocrFilePicked = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // so picking the same file twice fires onChange twice
    if (file) await reocr(await toBase64(file));
  };

  const contest = async (): Promise<void> => {
    if (!detail) return;
    setBusy(true);
    try {
      await apiClient.post(`/supplier-invoices/${detail.id}/contest`, {
        reason: t('supplierInvoices.contestDefault'),
      });
      push({ tone: 'success', message: t('supplierInvoices.contested') });
      await Promise.all([load(), loadDetail(detail.id)]);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t('supplierInvoices.title')}
        description={t('supplierInvoices.description')}
      />

      {ocr && !ocr.configured && (
        <p className="mb-4 rounded border border-warning/40 bg-warning/10 p-3 text-sm">
          {t('supplierInvoices.ocrNotConfigured')}
        </p>
      )}

      <div className="mb-6 flex flex-wrap items-end gap-2 rounded-lg border bg-muted/30 p-3">
        <div className="min-w-[14rem]">
          <label className="mb-1 block text-xs font-medium" htmlFor="po">
            {t('supplierInvoices.targetPo')}
          </label>
          <Select id="po" value={poId} onChange={(e) => setPoId(e.target.value)}>
            <option value="">{t('supplierInvoices.choosePo')}</option>
            {pos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.poNumber} — {p.supplierName}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-w-[10rem]">
          <label className="mb-1 block text-xs font-medium" htmlFor="invnum">
            {t('supplierInvoices.invoiceNumber')}
          </label>
          <Input
            id="invnum"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium" htmlFor="pdf">
            {t('supplierInvoices.pdf')}
          </label>
          <input id="pdf" ref={fileRef} type="file" accept="application/pdf" className="text-sm" />
        </div>
        <Button disabled={busy || !poId} onClick={() => void upload()}>
          {t('supplierInvoices.import')}
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <div className="overflow-hidden rounded-lg border">
          <ul className="max-h-[32rem] divide-y overflow-y-auto text-sm">
            {rows.map((r) => (
              <li
                key={r.id}
                onClick={() => setParams({ id: r.id })}
                className={`cursor-pointer px-3 py-2 hover:bg-muted/30 ${r.id === selectedId ? 'bg-muted/50' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">
                    {r.invoiceNumber ?? r.poNumber ?? '—'}
                  </span>
                  {r.varianceCount > 0 && <Badge variant="destructive">{r.varianceCount}</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {r.supplierName} · {money(r.totalHT)} · {t(`supplierInvoices.status.${r.status}`)}
                </div>
              </li>
            ))}
            {rows.length === 0 && (
              <li className="px-3 py-8 text-center text-muted-foreground">
                {t('supplierInvoices.empty')}
              </li>
            )}
          </ul>
        </div>

        {detail ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3">
              <div>
                <div className="font-semibold">{detail.invoiceNumber ?? '—'}</div>
                <div className="text-xs text-muted-foreground">
                  {detail.supplierName} · {detail.poNumber ?? '—'} · {t('supplierInvoices.engine')}:{' '}
                  {detail.ocrEngine ?? '—'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant={
                    detail.status === 'VALIDATED'
                      ? 'success'
                      : detail.status === 'CONTESTED'
                        ? 'destructive'
                        : 'secondary'
                  }
                >
                  {t(`supplierInvoices.status.${detail.status}`)}
                </Badge>
                {/* Offered on CONTESTED too: an invoice under dispute is the one most likely to
                    be there because the extraction was wrong. */}
                {detail.status !== 'VALIDATED' && ocr?.configured && (
                  <>
                    <input
                      ref={reocrFileRef}
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => void onReocrFilePicked(e)}
                    />
                    <Button
                      variant="outline"
                      disabled={busy}
                      onClick={() => void reocr()}
                      title={t('supplierInvoices.reocrHint', { engine: ocr.engine })}
                    >
                      {t('supplierInvoices.reocr')}
                    </Button>
                  </>
                )}
                {detail.status === 'IMPORTED' && (
                  <>
                    <Button variant="outline" disabled={busy} onClick={() => void contest()}>
                      {t('supplierInvoices.contest')}
                    </Button>
                    <Button
                      disabled={busy || !allAccepted}
                      onClick={() => void validate()}
                      title={allAccepted ? undefined : t('supplierInvoices.acceptFirst')}
                    >
                      {t('supplierInvoices.validate')}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* WAP applies to net-price suppliers only; say which model is in play before the
                operator presses a button that reprices the catalogue. */}
            <p className="rounded bg-muted/40 p-2 text-xs text-muted-foreground">
              {detail.pricingModel === 'NET_PRICE'
                ? t('supplierInvoices.wapExplain')
                : t('supplierInvoices.tariffExplain')}
              {detail.wapAppliedAt && ` — ${t('supplierInvoices.wapApplied')}`}
            </p>

            {flagged.length > 0 && detail.status === 'IMPORTED' && (
              <p className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm">
                {t('supplierInvoices.varianceBlock', { count: flagged.length })}
              </p>
            )}

            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2" colSpan={3}>
                      {t('supplierInvoices.col.extracted')}
                    </th>
                    <th className="border-l px-3 py-2" colSpan={2}>
                      {t('supplierInvoices.col.purchaseOrder')}
                    </th>
                    <th className="border-l px-3 py-2 text-right">
                      {t('supplierInvoices.col.variance')}
                    </th>
                  </tr>
                  <tr className="text-[0.68rem]">
                    <th className="px-3 py-1">{t('supplierInvoices.col.ref')}</th>
                    <th className="px-3 py-1 text-right">{t('supplierInvoices.col.qty')}</th>
                    <th className="px-3 py-1 text-right">{t('supplierInvoices.col.unitPrice')}</th>
                    <th className="border-l px-3 py-1">{t('supplierInvoices.col.product')}</th>
                    <th className="px-3 py-1 text-right">{t('supplierInvoices.col.poPrice')}</th>
                    <th className="border-l px-3 py-1 text-right">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {detail.lines.map((l) => (
                    <tr key={l.id} className={l.varianceFlag ? 'bg-destructive/5' : undefined}>
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs">{l.supplierArticleNo ?? '—'}</div>
                        {l.description && (
                          <div className="truncate text-xs text-muted-foreground">
                            {l.description}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">{Number(l.quantity)}</td>
                      <td className="px-3 py-2 text-right">{money(l.unitPriceHT)}</td>
                      <td className="border-l px-3 py-2">
                        {l.productId ? (
                          <>
                            <div className="truncate">{l.productName}</div>
                            <div className="text-xs text-muted-foreground">{l.productSku}</div>
                          </>
                        ) : (
                          // An unmatched line is exactly the case a human has to look at.
                          <Badge variant="warning">{t('supplierInvoices.unmatched')}</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">{money(l.poUnitPriceHT)}</td>
                      <td className="border-l px-3 py-2 text-right">
                        {l.variancePct == null ? (
                          '—'
                        ) : (
                          <span className={l.varianceFlag ? 'font-semibold text-destructive' : ''}>
                            {Number(l.variancePct) > 0 ? '+' : ''}
                            {Number(l.variancePct).toFixed(1)} %
                          </span>
                        )}
                        {l.varianceFlag && detail.status === 'IMPORTED' && (
                          <label className="mt-1 flex items-center justify-end gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={accepted.has(l.id)}
                              onChange={() =>
                                setAccepted((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(l.id)) next.delete(l.id);
                                  else next.add(l.id);
                                  return next;
                                })
                              }
                            />
                            {t('supplierInvoices.accept')}
                          </label>
                        )}
                      </td>
                    </tr>
                  ))}
                  {detail.lines.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                        {t('supplierInvoices.noLines')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
            {t('supplierInvoices.selectOne')}
          </div>
        )}
      </div>
    </div>
  );
}

async function toBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buf);
  // Chunked so a multi-MB PDF does not blow the argument limit of String.fromCharCode.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}
